import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Row, Col, CardBody, Card } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import DataTable from 'react-data-table-component';
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import { Tooltip } from "@mui/material";
import PatientsListFilter from "../../components/Table Filters/PatientsListFilter";
import PermissionAccess from '../../PermissionAccess/Access.js';
import { getFileUrl } from "../../utils/fileUtils";
import PatientICReviewModal from "./PatientICReviewModal";
import PatientICHistoryModal from "./PatientICHistoryModal";
import EditPatientModal from "./EditPatientModal";
import { SensitiveImage, SensitivePageBanner, SensitiveText } from "../../context/SensitiveInfoContext";
import ImagePreviewModal from "../../components/Modals/ImagePreviewModal";

const icStatusButtonStyle = (status) => {
  switch (status) {
    case "approved":
      return { borderColor: "#3ac47d", backgroundColor: "#1BD35A" };
    case "rejected":
      return { borderColor: "#FF0000", backgroundColor: "red" };
    case "re-submission":
      return { borderColor: "#f7b924", backgroundColor: "#f7b924", color: "#222" };
    default:
      return { borderColor: "#30b1ff", backgroundColor: "#30b1ff" };
  }
};

const PatientsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [discountcoupons, setDiscountcoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterICVerification, setFilterICVerification] = useState('');
  const [hoveredImage, setHoveredImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const [selectedId, setSelectedId] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [showICReviewModal, setShowICReviewModal] = useState(false);
  const [showICHistoryModal, setShowICHistoryModal] = useState(false);
  const [showModalEditPatient, setShowModalEditPatient] = useState(false);

  const [pageLimit, setPageLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();
  const hasPermission = (value) => value === 1 || value === "1" || value === true;
  const canView = super_admin || hasPermission(current_module?.ViewDetails) || hasPermission(current_module?.ViewData);
  const canUpdateAccountStatus =
    super_admin || hasPermission(current_module?.ChangeStatus) || hasPermission(current_module?.EditData);
  const canRegeneratePassword =
    super_admin || hasPermission(current_module?.RegeneratePassword) || hasPermission(current_module?.EditData);
  const canReviewIC = super_admin || hasPermission(current_module?.EditData) || canView;
  const canDelete = super_admin || hasPermission(current_module?.DeleteData);

  const handleToggleModalEditPatient = (id) => {
    setShowModalEditPatient(!showModalEditPatient);
    setSelectedId(id);
  };

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchDiscountcoupons(0, pageLimit);
  }, [pageLimit]);


  function fetchDiscountcoupons(page_index, page_limit, text, status, icVerification) {
    setLoading(true);
    var offset = (page_index - 1) * page_limit;
    if (offset < 1) {
      offset = 0;
    }

    const searchText = text !== undefined ? text : filterText;
    const activeStatus = status !== undefined ? status : filterStatus;
    const icStatus = icVerification !== undefined ? icVerification : filterICVerification;

    DiscountCouponService.getAllPatients(
      authData.sessionID,
      offset,
      page_limit,
      null,
      searchText,
      activeStatus,
      icStatus
    ).then(
      (response) => {
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          setDiscountcoupons(response.data.data);
          setTotalRows(response.data.count.count);
        }
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      }
    );
  }


  function regeneratePassword(_id) {
    let msg = t("Are you sure you want to regenerate password for this patient?")
    Alerts.swalConfirmGeneralAlert(msg).then(res => {
      if (res === true) {
        sendRegeneratePassword(_id)
      }
    })
  };

  function sendRegeneratePassword(id) {
    setLoading(true);
    DiscountCouponService.regeneratePassword(authData.sessionID, id).then((response) => {
      setLoading(false);
      if (response.data.status) {
        if (response.data.message !== '') {
          Alerts.swalSuccessAlert(response.data.message)
        }
      }
      else {
        setLoading(false);
        if (response.data.message !== '') {
          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          }
          else {
            Alerts.swalErrorAlert(response.data.message);
          }
        }
        else {
          Alerts.swalErrorAlert(t("Error: Can't delete the Section."))
        }
      }
    },
      (error) => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Delete operation failed"))
      })
  };

  function confirmDelete(_id) {
    const msg = t("Are you sure you want to delete this patient?");
    Alerts.swalConfirmDeleteAlert(msg).then((res) => {
      if (res === true) {
        sendDelete(_id);
      }
    });
  }

  function sendDelete(id) {
    setLoading(true);
    DiscountCouponService.deletePatient(authData.sessionID, id).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          Alerts.swalSuccessAlert(
            response.data.message || t("Patient deleted successfully.")
          );
          refreshList();
        } else if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          Alerts.swalErrorAlert(
            response.data.message || t("Error: Can't delete the Patient.")
          );
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Delete operation failed"));
      }
    );
  }

  function confirmRestore(_id) {
    const msg = t("Are you sure you want to restore this patient?");
    Alerts.swalConfirmGeneralAlert(msg).then((res) => {
      if (res === true) {
        sendRestore(_id);
      }
    });
  }

  function sendRestore(id) {
    setLoading(true);
    DiscountCouponService.restorePatient(authData.sessionID, id).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          Alerts.swalSuccessAlert(
            response.data.message || t("Patient restored successfully.")
          );
          refreshList();
        } else if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          Alerts.swalErrorAlert(
            response.data.message || t("Error: Can't restore the Patient.")
          );
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Restore operation failed"));
      }
    );
  }

  const openICReview = (row) => {
    setSelectedId(row._id);
    setSelectedName(row.full_name || "");
    setShowICReviewModal(true);
  };

  const openICHistory = (row) => {
    setSelectedId(row._id);
    setSelectedName(row.full_name || "");
    setShowICHistoryModal(true);
  };

  const refreshList = () => {
    fetchDiscountcoupons(
      currentPage || 0,
      pageLimit,
      filterText,
      filterStatus,
      filterICVerification
    );
  };

  const mycolumns = [
    {
      name: t('Full Name'),
      selector: "full_name",
      width: "16%",
      sortable: true,
    },

    {
      name: t('Profile Image'),
      width: "12%",
      selector: (row) => {
        if (row.image) {
          const imageUrl = getFileUrl(row.image);
          return (
            <SensitiveImage
              src={imageUrl}
              alt={row.full_name}
              style={{ width: "50px", height: "50px", borderRadius: "100%", cursor: "pointer" }}
              onClick={() => {
                setHoveredImage(imageUrl);
                setPreviewTitle(t("Profile Image"));
              }}
            />
          );
        } else {
          return <span>{t('No Image')}</span>;
        }
      },
      sortable: false,
    },
    
    {
      name: t('Email'),
      selector: (row) =>
        row.status === "Deleted" && row.deleted_email
          ? row.deleted_email
          : row.email,
      width: "16%",
      sortable: true,
    },

    {
      name: t('NRIC'),
      selector: (row) => <SensitiveText value={row.medical_assistant_id} />,
      width: "12%",
      sortable: false,
    },

    {
      name: t('Status'),
      width: '12%',
      selector: (row) => {
        const status = row.status || "Pending";
        let style = { borderColor: '#FF0000', backgroundColor: 'red' };
        if (status === "Approved") {
          style = { borderColor: '#3ac47d', backgroundColor: '#1BD35A' };
        } else if (status === "Pending") {
          style = { borderColor: '#FFA500', backgroundColor: '#FFD700', color: '#222' };
        } else if (status === "Deleted") {
          style = { borderColor: '#6c757d', backgroundColor: '#6c757d' };
        }
        return (
          <div>
            <button
              type="button"
              style={style}
              className="btn btn-sm btn-hover-shine me-3 text-white"
            >
              {status}
            </button>
          </div>
        );
      },
      sortable: true,
    },

    {
      name: t('IC Verification'),
      width: "12%",
      selector: (row) => {
        const status = row.IC_Verification || "pending";
        return (
          <button
            type="button"
            style={icStatusButtonStyle(status)}
            className="btn btn-sm btn-hover-shine text-white"
          >
            {status}
          </button>
        );
      },
      sortable: false,
    },

    {
      name: t('Actions'),
      width: "20%",
      selector: (row) => {
        return (
          <div>
            {canView &&
              <Tooltip placement="top" title={`${t("View")} ${t("Details")}`} arrow>
                <NavLink to={`/patient-details/${row._id}`}>
                  <i className="pe-7s-look text-primary btn-icon-wrapper fs-5 ViewDetails"> </i>
                </NavLink>
              </Tooltip>
            }

            {row.status === "Deleted" ? (
              canDelete && (
                <Tooltip placement="top" title={t("Restore")} arrow>
                  <i
                    className="pe-7s-back text-success btn-icon-wrapper fs-5"
                    style={{ cursor: "pointer", marginLeft: "10px" }}
                    onClick={() => confirmRestore(row._id)}
                  />
                </Tooltip>
              )
            ) : (
              <>
                {canUpdateAccountStatus &&
                  <Tooltip
                    placement="top"
                    title={`${t("Update")} ${t("Status")}`}
                    arrow
                    onClick={() => handleToggleModalEditPatient(row._id)}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="pe-7s-pen btn-icon-wrapper text-primary fs-5 p-1 ChangeStatus"> </i>
                  </Tooltip>
                }

                {canReviewIC &&
                  <Tooltip placement="top" title={t("IC Review")} arrow>
                    <i
                      className="pe-7s-check text-success btn-icon-wrapper fs-5"
                      style={{ cursor: "pointer", marginLeft: "10px" }}
                      onClick={() => openICReview(row)}
                    />
                  </Tooltip>
                }

                {canView &&
                  <Tooltip placement="top" title={t("View History")} arrow>
                    <i
                      className="pe-7s-clock text-info btn-icon-wrapper fs-5"
                      style={{ cursor: "pointer", marginLeft: "10px" }}
                      onClick={() => openICHistory(row)}
                    />
                  </Tooltip>
                }

                {canRegeneratePassword &&
                  <Tooltip placement="top" title={`${t("Regenerate Password")}`} arrow>
                    <i
                      className="pe-7s-key text-primary btn-icon-wrapper fs-5 RegeneratePassword"
                      style={{ cursor: "pointer", marginLeft: '10px' }}
                      onClick={() => regeneratePassword(row._id)}
                    />
                  </Tooltip>
                }

                {canDelete &&
                  <Tooltip placement="top" title={`${t("Delete")} ${t("Patient")}`} arrow>
                    <i
                      className="pe-7s-trash text-danger btn-icon-wrapper fs-5 DeleteData"
                      style={{ cursor: "pointer", marginLeft: "10px" }}
                      onClick={() => confirmDelete(row._id)}
                    />
                  </Tooltip>
                }
              </>
            )}
          </div>
        );
      }
    },
  ];

  const handleSearch = (searchText, status, icVerification) => {
    setFilterText(searchText);
    setFilterStatus(status);
    setFilterICVerification(icVerification || "");
    fetchDiscountcoupons(0, pageLimit, searchText, status, icVerification || "");
  };

  const handleClear = () => {
    setFilterText('');
    setFilterStatus('');
    setFilterICVerification('');
    fetchDiscountcoupons(0, pageLimit, "", "", "");
  };
  const filteredItems = discountcoupons;

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <PatientsListFilter
        onSearch={handleSearch}
        onClear={handleClear}
        filterText={filterText}
        filterStatus={filterStatus}
        filterICVerification={filterICVerification}
      />
    );
  }, [filterText, filterStatus, filterICVerification]);


  function handlePageChange(page_index) {
    setCurrentPage(page_index)
    fetchDiscountcoupons(page_index, pageLimit, filterText, filterStatus, filterICVerification);
  }

  async function handlePerRowsChange(newPageLimit, page_index) {
    setPageLimit(newPageLimit);
  }

  return (
    <Fragment>

      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-news-paper icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {`${t("Patients")} ${t("Management")}`}
                        <div className="page-title-subheading">
                          {`${t("Details of")} ${t("Patients")}`}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <PatientICReviewModal
                        show={showICReviewModal}
                        patientId={selectedId}
                        onClose={() => setShowICReviewModal(false)}
                        onSuccess={refreshList}
                      />
                      <PatientICHistoryModal
                        show={showICHistoryModal}
                        patientId={selectedId}
                        patientName={selectedName}
                        onClose={() => setShowICHistoryModal(false)}
                      />
                      <EditPatientModal
                        onSuccess={refreshList}
                        showModalEditPatient={showModalEditPatient}
                        handleToggleModalEditPatient={() => handleToggleModalEditPatient(null)}
                        id={selectedId}
                      />
                    </div>
                  </div>
                </div>
                <Row>
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <SensitivePageBanner />
                          <div className="mb-3">
                            {subHeaderComponentMemo}
                          </div>
                          <DataTable
                            data={filteredItems}
                            columns={mycolumns}
                            pagination
                            fixedHeader
                            fixedHeaderScrollHeight="400px"
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationResetDefaultPage={resetPaginationToggle}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
      </div>

      <ImagePreviewModal
        show={!!hoveredImage}
        onClose={() => {
          setHoveredImage(null);
          setPreviewTitle("");
        }}
        src={hoveredImage}
        title={previewTitle}
      />

    </Fragment>
  );
};

export default PatientsList;
